import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { State } from 'redux/initialState';
import { store } from 'redux/store';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';

import menuTypes from 'constants/menuTypes';
import sizes from 'constants/sizes';
import { setModal } from 'modules/_shared/redux/modalActions';
import { getBeneficiary, getBeneficiaries } from 'modules/incentivesMarket/api/beneficiaryActions';
import {
  IncentiveOffer,
  IncentiveOfferOperation,
  IncentivesMarketBeneficiary,
} from 'modules/incentivesMarket/types';

import { SocietyEquityMarketStatus } from 'types/Society';
import {
  IncentivesMarketContextType,
  MarketInfo,
  BeneficiaryActiveOffers,
  BeneficiaryOperations,
} from '../types/IncentivesMarketContextType';
import { incentivesMarketInitValue } from '../constants/IncentivesMarketInitValue';
import {
  TRADING_OFFERS_PAGE_SIZE,
  TRADING_OFFERS_FILTERS,
} from '../constants/incentiveOffersConfig';
import { useGetIncentiveOffers } from '../hooks/useGetIncentiveOffers';
import { getIncentiveOfferOperations } from '../redux/incentiveOfferOperationActions';
import HowItWorksModal from '../modals/HowItWorksModal';
import { isOfferActive } from '../utils/getOfferStatus';
import calculateOffersSummary from '../utils/calculateOffersSummary';
import { filterSellOffers, filterBuyOffers } from '../utils/filterOffersByType';

const getBeneficiaryId = (
  party: string | Partial<IncentivesMarketBeneficiary> | undefined
): string | null => {
  if (!party) return null;
  if (typeof party === 'string') return party;
  return (party as IncentivesMarketBeneficiary)?._id ?? null;
};

export const IncentivesMarketContext = createContext<IncentivesMarketContextType>(
  incentivesMarketInitValue
);

export const IncentivesMarketProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { societyId } = useParams<{
    societyId: string;
  }>();
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const beneficiaryId = queryParams.get('beneficiaryId');

  const actualSociety = useSelector(
    ({ society }: State) => society?.actualSociety
  );
  const user = useSelector(({ user }: State) => user);
  const isAdmin = useSelector(
    (state: State) => state.society?.role?.isAdmin ?? false
  );
  const isBeneficiary = useSelector(
    (state: State) => state.society?.role?.isBeneficiary ?? false
  );
  const incentiveOffers = useSelector(
    ({ incentiveOffers }: State) => (incentiveOffers ?? []) as IncentiveOffer[]
  );

  const { referenceInfo } = incentivesMarketInitValue.data;
  const recentActivity = useMemo(() => [], []);
  const [incentiveOfferOperations, setIncentiveOfferOperations] = useState<
    IncentiveOfferOperation[]
  >([]);
  const [beneficiary, setBeneficiary] = useState<IncentivesMarketBeneficiary | null>(null);

  useGetActualSociety(societyId);
  useGetMenu(menuTypes.NULL);

  useEffect(() => {
    if (!actualSociety || !societyId) return;

    const controller = new AbortController();

    const loadBeneficiary = async () => {
      try {
        if (beneficiaryId) {
          const result = await store.dispatch(
            getBeneficiary(beneficiaryId, { societyId })
          );
          if (!controller.signal.aborted) {
            setBeneficiary(result ? (result as IncentivesMarketBeneficiary) : null);
          }
        } else if (isBeneficiary && user?._id) {
          const result = (await store.dispatch(
            getBeneficiaries(societyId, { userId: user._id, items: 1 })
          )) as { data?: IncentivesMarketBeneficiary[] };
          const beneficiaries = result?.data ?? [];
          if (!controller.signal.aborted && beneficiaries.length > 0) {
            setBeneficiary(beneficiaries[0]);
          } else {
            setBeneficiary(null);
          }
        } else {
          setBeneficiary(null);
        }
      } catch {
        if (!controller.signal.aborted) setBeneficiary(null);
      }
    };

    loadBeneficiary();

    return () => controller.abort();
  }, [actualSociety, societyId, beneficiaryId, isBeneficiary, user?._id]);

  useEffect(() => {
    if (!actualSociety || !societyId) return;

    const controller = new AbortController();

    const fetchOperations = async () => {
      try {
        const result = await store.dispatch(
          getIncentiveOfferOperations({ societyId })
        );
        if (!controller.signal.aborted && result?.data) {
          setIncentiveOfferOperations(result.data);
        }
      } catch {
        // Error handled by action
      }
    };

    fetchOperations();

    return () => controller.abort();
  }, [actualSociety, societyId]);

  const { isLoading: isIncentiveOffersLoading } = useGetIncentiveOffers({
    societyId: actualSociety ? societyId : undefined,
    page: 1,
    rowsPerPage: TRADING_OFFERS_PAGE_SIZE,
    filters: TRADING_OFFERS_FILTERS,
  });

  const isReadOnly = false;

  const isMarketClosed = useMemo(() => {
    const status = actualSociety?.configuration?.incentivesMarket?.status;
    return (
      status === SocietyEquityMarketStatus.CLOSED ||
      status === SocietyEquityMarketStatus.UNAVAILABLE
    );
  }, [actualSociety?.configuration?.incentivesMarket?.status]);

  const sellOffers = useMemo(
    () => filterSellOffers(incentiveOffers),
    [incentiveOffers]
  );

  const buyOffers = useMemo(
    () => filterBuyOffers(incentiveOffers),
    [incentiveOffers]
  );

  const sellOffersSummary = useMemo(
    () =>
      calculateOffersSummary(
        sellOffers.filter((offer) => isOfferActive(offer))
      ),
    [sellOffers]
  );

  const buyOffersSummary = useMemo(
    () =>
      calculateOffersSummary(buyOffers.filter((offer) => isOfferActive(offer))),
    [buyOffers]
  );

  const marketInfo: MarketInfo = useMemo(() => {
    if (!sellOffers && !buyOffers) return incentivesMarketInitValue.data.marketInfo;

    const activeSellOffers = sellOffers.filter((offer) => isOfferActive(offer));
    const activeBuyOffers = buyOffers.filter((offer) => isOfferActive(offer));

    const totalOffers =
      (activeSellOffers?.length ?? 0) + (activeBuyOffers?.length ?? 0);
    const sellCount = activeSellOffers?.length ?? 0;
    const buyCount = activeBuyOffers?.length ?? 0;
    const sellAmount = sellOffersSummary?.totalAmount ?? 0;
    const buyAmount = buyOffersSummary?.totalAmount ?? 0;

    return { totalOffers, sellCount, buyCount, sellAmount, buyAmount };
  }, [sellOffers, buyOffers, sellOffersSummary, buyOffersSummary]);

  const beneficiaryActiveOffers: BeneficiaryActiveOffers = useMemo(() => {
    if (!sellOffers && !buyOffers)
      return incentivesMarketInitValue.data.beneficiaryActiveOffers;

    const filteredSellOffers = beneficiaryId
      ? sellOffers?.filter(
          (offer) => (offer.beneficiary as IncentivesMarketBeneficiary)._id === beneficiaryId
        )
      : sellOffers;

    const filteredBuyOffers = beneficiaryId
      ? buyOffers?.filter(
          (offer) => (offer.beneficiary as IncentivesMarketBeneficiary)._id === beneficiaryId
        )
      : buyOffers;

    const total =
      (filteredSellOffers?.length ?? 0) + (filteredBuyOffers?.length ?? 0);
    const sellCount = filteredSellOffers?.length ?? 0;
    const buyCount = filteredBuyOffers?.length ?? 0;

    const sellAmount =
      filteredSellOffers?.reduce(
        (acc, offer) => acc + offer.unitsCount.total * offer.unitPrice,
        0
      ) ?? 0;
    const buyAmount =
      filteredBuyOffers?.reduce(
        (acc, offer) => acc + offer.unitsCount.total * offer.unitPrice,
        0
      ) ?? 0;

    return { total, sellCount, buyCount, sellAmount, buyAmount };
  }, [sellOffers, buyOffers, beneficiaryId]);

  const beneficiaryShares = useMemo(() => {
    const base = incentivesMarketInitValue.data.beneficiaryShares;
    const total = beneficiary?.sharesCount?.actual ?? 0;
    return { ...base, total };
  }, [beneficiary?.sharesCount?.actual]);

  const userId = user?._id ?? '';
  const beneficiaryOperations: BeneficiaryOperations = useMemo(() => {
    const userOps = incentiveOfferOperations.filter((op) => {
      const buyerId = getBeneficiaryId(op.buyer);
      const sellerId = getBeneficiaryId(op.seller);
      return buyerId === userId || sellerId === userId;
    });

    const sellCount = userOps.filter(
      (op) => getBeneficiaryId(op.seller) === userId
    ).length;
    const buyCount = userOps.filter(
      (op) => getBeneficiaryId(op.buyer) === userId
    ).length;
    const sellAmount = userOps
      .filter((op) => getBeneficiaryId(op.seller) === userId)
      .reduce((acc, op) => acc + (op.totalPrice ?? 0), 0);
    const buyAmount = userOps
      .filter((op) => getBeneficiaryId(op.buyer) === userId)
      .reduce((acc, op) => acc + (op.totalPrice ?? 0), 0);

    return {
      total: userOps.length,
      sellCount,
      buyCount,
      sellAmount,
      buyAmount,
    };
  }, [incentiveOfferOperations, userId]);

  const beneficiaryQuery = beneficiaryId ? `?beneficiaryId=${beneficiaryId}` : '';

  const handleCreateSellOffer = useCallback(() => {
    history.push(
      beneficiaryId
        ? `/crear-oferta-venta-incentivos/${societyId}/${beneficiaryId}`
        : `/crear-oferta-venta-incentivos/${societyId}`
    );
  }, [history, societyId, beneficiaryId]);

  const handleCreateBuyOffer = useCallback(() => {
    history.push(
      beneficiaryId
        ? `/crear-oferta-compra-incentivos/${societyId}/${beneficiaryId}`
        : `/crear-oferta-compra-incentivos/${societyId}`
    );
  }, [history, societyId, beneficiaryId]);

  const handleViewSellOffers = useCallback(() => {
    history.push(`/ofertas-venta-incentivos/${societyId}${beneficiaryQuery}`);
  }, [history, societyId, beneficiaryQuery]);

  const handleViewBuyOffers = useCallback(() => {
    history.push(`/ofertas-compra-incentivos/${societyId}${beneficiaryQuery}`);
  }, [history, societyId, beneficiaryQuery]);

  const handleViewOfferDetail = useCallback(
    (offerId: string) => {
      history.push(`/detalle-oferta-incentivos/${societyId}/${offerId}${beneficiaryQuery}`);
    },
    [history, societyId, beneficiaryQuery]
  );

  const handleViewMyShares = useCallback(() => {
    history.push(`/mis-acciones/${societyId}${beneficiaryQuery}`);
  }, [history, societyId, beneficiaryQuery]);

  const handleViewMyOffers = useCallback(() => {
    history.push(`/ofertas-incentivos/${societyId}/${user._id}${beneficiaryQuery}`);
  }, [history, societyId, user, beneficiaryQuery]);

  const handleViewMyOperations = useCallback(() => {
    history.push(`/mis-operaciones-incentivos/${societyId}${beneficiaryQuery}`);
  }, [history, societyId, beneficiaryQuery]);

  const handleOpenHowItWorks = useCallback(() => {
    dispatch(
      setModal(<HowItWorksModal size={sizes.LG} societyId={societyId} />)
    );
  }, [dispatch, societyId]);

  const contextValue = useMemo(
    () => ({
      data: {
        societyId: societyId || '',
        societyName: actualSociety?.name || '',
        beneficiaryShares,
        beneficiaryActiveOffers,
        beneficiaryOperations,
        marketInfo,
        sellOffers,
        buyOffers,
        sellOffersSummary,
        buyOffersSummary,
        recentActivity,
        referenceInfo,
        isAdmin,
        isBeneficiary,
        isReadOnly,
        isLoading: isIncentiveOffersLoading || !actualSociety,
        isMarketClosed,
      },
      actions: {
        handleCreateSellOffer,
        handleCreateBuyOffer,
        handleViewSellOffers,
        handleViewBuyOffers,
        handleViewOfferDetail,
        handleViewMyShares,
        handleViewMyOffers,
        handleViewMyOperations,
        handleOpenHowItWorks,
      },
    }),
    [
      societyId,
      actualSociety,
      beneficiaryShares,
      beneficiaryActiveOffers,
      beneficiaryOperations,
      marketInfo,
      sellOffers,
      buyOffers,
      sellOffersSummary,
      buyOffersSummary,
      recentActivity,
      referenceInfo,
      isAdmin,
      isBeneficiary,
      isReadOnly,
      isIncentiveOffersLoading,
      isMarketClosed,
      handleCreateSellOffer,
      handleCreateBuyOffer,
      handleViewSellOffers,
      handleViewBuyOffers,
      handleViewOfferDetail,
      handleViewMyShares,
      handleViewMyOffers,
      handleViewMyOperations,
      handleOpenHowItWorks,
    ]
  );

  return (
    <IncentivesMarketContext.Provider value={contextValue}>
      {children}
    </IncentivesMarketContext.Provider>
  );
};

export const useIncentivesMarket = () => {
  const context = useContext(IncentivesMarketContext);
  if (!context) {
    throw new Error(
      'useIncentivesMarket must be used within an IncentivesMarketProvider'
    );
  }
  return context;
};
